import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useLogger } from "@/logging/client/LoggerContext";
import { assignLocation } from "@/setup/frontend/assignLocation";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";
import {
	useAmplifyAuth,
	type AmplifyAuthFunctions,
} from "../components/AmplifyAuthContext";
import { buildFreshAuthLoginUrl } from "../utils/buildFreshAuthLoginUrl";
import { hasSelfServiceScope } from "../utils/hasSelfServiceScope";

export interface FreshAuth {
	isLoading: boolean;
	isFresh: boolean;
	needsFreshAuth: boolean;
	ensureFreshAuth: () => Promise<boolean>;
	signInAgain: () => Promise<void>;
}

// RemoteAuthGuard treats a cached current user as signed in and would send the
// login page straight back to "/", dropping the redirect.
const currentUserCacheKey = "vcg-current-user";

export function useFreshAuth(): FreshAuth {
	const { fetchAuthSession, signOut } = useAmplifyAuth();
	const queryClient = useQueryClient();
	const cacheStorage = useCacheStorage();
	const logger = useLogger();

	const freshAuthQueryOptions = buildFreshAuthQueryOptions(fetchAuthSession);
	const freshAuthQuery = useQuery(freshAuthQueryOptions);

	// Self-service actions call this right before running, because a token that
	// was fresh when the screen opened may have been refreshed since.
	const ensureFreshAuth = () =>
		queryClient.fetchQuery(freshAuthQueryOptions).catch(() => false);

	const signInAgain = async () => {
		logger.info("FreshAuth: signing out to get a fresh sign-in");
		await logger.flush().catch((error) => console.error(error));
		await signOut().catch((error) => {
			logger.error(
				`FreshAuth: signOut - error - ${error instanceof Error ? error.message : "unknown error"}`,
			);
		});
		await cacheStorage.removeItem(currentUserCacheKey).catch(() => null);
		assignLocation(buildFreshAuthLoginUrl(window.location));
	};

	return {
		isLoading: freshAuthQuery.isPending,
		isFresh: freshAuthQuery.data === true,
		needsFreshAuth: freshAuthQuery.isError || freshAuthQuery.data === false,
		ensureFreshAuth,
		signInAgain,
	};
}

function buildFreshAuthQueryOptions(
	fetchAuthSession: AmplifyAuthFunctions["fetchAuthSession"],
) {
	return queryOptions({
		queryKey: ["freshAuth"],
		queryFn: async () => hasSelfServiceScope(await fetchAuthSession()),
		staleTime: 0,
	});
}
